import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useBranding, brandLine } from '../hooks/useBranding';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LayoutDashboard, Users, Calendar, Filter, Loader2, RefreshCw, Rocket, BarChart3, FileCheck, Target, Printer, Activity,
  Landmark, MoreHorizontal, Tv, Volume2, VolumeX, GitBranch, Sun, Moon,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { backendUrl, getCurrentMonth, getMonthOptions } from '../components/gameplan/gameplanUtils';
import { ProjectionsView } from '../components/gameplan/ProjectionsView';
import { MetricsView } from '../components/gameplan/MetricsView';
import { ApprovalsView } from '../components/gameplan/ApprovalsView';
import { FilePanel } from '../components/gameplan/credit/FilePanel';
import GoalsSettingsDialog from '../components/gameplan/GoalsSettingsDialog';
import PipelineCommandCentre from '../components/gameplan/PipelineCommandCentre';
import PrintReportDialog from '../components/gameplan/PrintReportDialog';
import PerformanceCommandCentre from '../components/gameplan/PerformanceCommandCentre';
import LivePerformanceDashboard from '../components/gameplan/LivePerformanceDashboard';
import SettlementsView from '../components/gameplan/SettlementsView';
import AddDealDialog from '../components/gameplan/AddDealDialog';
import PipelineTab from '../components/gameplan/jarvis/PipelineTab';
import { SettlementPanels } from '../components/gameplan/jarvis/PerformanceTab';
import CommandCentre from '../components/gameplan/command/CommandCentre';
import { CelebrationProvider, useCelebrations } from '../components/gameplan/jarvis/Celebrations';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import '../components/gameplan/jarvis/jarvis.css';
import '../components/gameplan/jarvis/gameplan-light.css';
import useGpTheme from '../components/gameplan/jarvis/useGpTheme';

const NAV = [
  { key: 'dashboard',   label: 'Overview',         icon: LayoutDashboard, mobile: 'Overview' },
  { key: 'flow',        label: 'Pipeline',         icon: GitBranch,       mobile: 'Pipeline' },
  { key: 'lodged',      label: 'Approvals',        icon: FileCheck,       mobile: 'Approvals' },
  { key: 'settlements', label: 'Settlements',      icon: Landmark,        mobile: 'Settle.' },
  { key: 'live',        label: 'Weekly Game Plan', icon: Activity,        mobile: 'Weekly' },
];
const MORE = [
  { key: 'pipeline',    label: 'Pipeline (classic)', icon: BarChart3 },
  { key: 'projections', label: 'Projections', icon: Rocket },
  { key: 'metrics',     label: 'Metrics',     icon: BarChart3 },
];
const POLL_MS = 30_000;

const Clock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 10_000); return () => clearInterval(id); }, []);
  return <span className="gpj-pill" data-testid="gpj-clock">{now.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })} · {now.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}</span>;
};

/* Pill tabs with a sliding indicator. */
const Tabs = ({ active, onChange }) => {
  const wrap = useRef(null);
  const [ind, setInd] = useState({ x: 4, w: 0 });
  const isMore = MORE.some((m) => m.key === active);
  useLayoutEffect(() => {
    const el = wrap.current?.querySelector(`[data-tabkey="${isMore ? 'more' : active}"]`);
    if (el) setInd({ x: el.offsetLeft, w: el.offsetWidth });
  }, [active, isMore]);
  return (
    <nav className="gpj-tabs" role="tablist" ref={wrap} data-testid="gameplan-nav">
      <span className="gpj-tab-ind" style={{ transform: `translateX(${ind.x}px)`, width: ind.w }} />
      {NAV.map(({ key, label, mobile }) => (
        <button key={key} role="tab" aria-selected={active === key} className="gpj-tab" data-tabkey={key} onClick={() => onChange(key)} data-testid={`tab-${key}`}>
          <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{mobile}</span>
        </button>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button role="tab" aria-selected={isMore} className="gpj-tab" data-tabkey="more" data-testid="tab-more">
            <span>{MORE.find((m) => m.key === active)?.label || 'More'}</span><MoreHorizontal style={{ width: 14, height: 14 }} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {MORE.map(({ key, label, icon: Icon }) => (
            <DropdownMenuItem key={key} onClick={() => onChange(key)} data-testid={`tab-${key}`}><Icon className="w-4 h-4 mr-2" />{label}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
};

const ThemeToggle = ({ theme, onToggle }) => (
  <button type="button" className={cn('gpj-theme', theme === 'light' && 'light')} onClick={onToggle} role="switch" aria-checked={theme === 'light'}
    title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'} data-testid="gpj-theme-toggle">
    <i aria-hidden="true" />
    <span className={theme !== 'light' ? 'on' : ''}><Moon /></span>
    <span className={theme === 'light' ? 'on' : ''}><Sun /></span>
  </button>
);

const GamePlanContent = () => {
  const { token, user } = useAuth();
  const { theme, toggle: toggleTheme, switching } = useGpTheme(token);
  const brand = useBranding();
  const readOnly = (user?.role || '').toLowerCase() === 'broker_support';
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [showGoalsDialog, setShowGoalsDialog] = useState(false);
  const [showPrintReport, setShowPrintReport] = useState(false);
  const [showCommandCentre, setShowCommandCentre] = useState(false);
  const [brokerFilter, setBrokerFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [boardData, setBoardData] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [projectionsData, setProjectionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [options, setOptions] = useState({ brokers: [], loanTypes: [], leadSources: [], stages: [] });
  const [lastRefresh, setLastRefresh] = useState(() => new Date());
  const cel = useCelebrations();
  const ingestFeed = cel?.ingestFeed;
  const [panelDealId, setPanelDealId] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const openFile = useCallback((file) => setPanelDealId(file?.deal_id || file?.id || null), []);
  const openEditDeal = useCallback((deal) => { setPanelDealId(null); setEditingDeal(deal); }, []);
  const [pipelineStage, setPipelineStage] = useState('all');
  const [pipelineFocus, setPipelineFocus] = useState(0);
  // Pipeline health row click: Settled → Settlements tab; any stage → Pipeline tab filtered to it.
  const goToStage = useCallback((key) => {
    if (key === 'settled') { setActiveView('settlements'); return; }
    setPipelineStage(key); setPipelineFocus((n) => n + 1); setActiveView('flow');
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const id = sp.get('deal');
    if (id) { setPanelDealId(id); window.history.replaceState({}, '', window.location.pathname); }
  }, []);

  // /game-plan?test-alert=lodged|approved|settled fires a sample alert on load (no figures change).
  const testAlert = cel?.test;
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('test-alert');
    if (!t || !testAlert || loading) return;
    window.history.replaceState({}, '', window.location.pathname);
    setTimeout(() => testAlert(['lodged', 'approved', 'settled'].includes(t) ? t : 'lodged'), 900);
  }, [testAlert, loading]);

  const monthOptions = getMonthOptions();
  const headers = { Authorization: `Bearer ${token}` };

  const openFileOnBoard = useCallback((file) => {
    if (file?.month && monthOptions.some((m) => m.value === file.month)) setSelectedMonth(file.month);
    setActiveView('dashboard');
    let tries = 0;
    const tick = () => {
      const el = document.querySelector(`[data-deal-id="${file?.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('glow');
        setTimeout(() => el.classList.remove('glow'), 2500);
      } else if (tries++ < 20) setTimeout(tick, 250);
    };
    setTimeout(tick, 300);
  }, [monthOptions]);

  const fetchOptions = async () => {
    try {
      const [brokers, loanTypes, leadSources, stages] = await Promise.all([
        fetch(`${backendUrl}/api/gameplan/options/brokers`, { headers }).then((r) => r.json()),
        fetch(`${backendUrl}/api/gameplan/options/loan-types`, { headers }).then((r) => r.json()),
        fetch(`${backendUrl}/api/gameplan/options/lead-sources`, { headers }).then((r) => r.json()),
        fetch(`${backendUrl}/api/gameplan/options/stages`, { headers }).then((r) => r.json()),
      ]);
      setOptions({ brokers, loanTypes, leadSources, stages });
    } catch (err) { console.error(err); }
  };

  // Keep the last good data on screen if a fetch fails; show a subtle stale indicator.
  const fetchData = useCallback(async (opts = {}) => {
    if (!token) return;
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    setSpinning(true);
    try {
      const params = new URLSearchParams();
      if (brokerFilter) params.append('broker', brokerFilter);
      if (sourceFilter) params.append('lead_source', sourceFilter);
      const authHeaders = { Authorization: `Bearer ${token}` };
      const ok = (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); };
      const [board, live, projections] = await Promise.all([
        fetch(`${backendUrl}/api/gameplan/board/${selectedMonth}?${params}`, { headers: authHeaders }).then(ok),
        fetch(`${backendUrl}/api/gameplan/live/overview?month=${selectedMonth}&${params}`, { headers: authHeaders }).then(ok),
        fetch(`${backendUrl}/api/gameplan/projections`, { headers: authHeaders }).then(ok),
      ]);
      setBoardData(board);
      setLiveData(live);
      ingestFeed?.(live.feed, { lodged: board.monthly_lodged_amount, lodgeTarget: board.monthly_target_amount, approved: board.approved_loan_amount, settled: live.settled?.amount, settleTarget: live.settled?.target });
      setProjectionsData(projections);
      setLastRefresh(new Date());
      setStale(false);
    } catch (err) {
      console.error(err);
      setStale(true);
    } finally {
      if (!silent) setLoading(false);
      setTimeout(() => setSpinning(false), 800);
    }
  }, [selectedMonth, brokerFilter, sourceFilter, token, ingestFeed]);

  const handleSaveDeal = useCallback(async (dealId, data) => {
    try {
      const res = await fetch(`${backendUrl}/api/gameplan/deals/${dealId}`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditingDeal(null);
      fetchData({ silent: true }); bump();
      toast.success('Deal updated');
    } catch (e) { toast.error('Failed to update deal'); }
  }, [token, bump, fetchData]);

  useEffect(() => { if (token) fetchOptions(); }, [token]); // eslint-disable-line
  useEffect(() => { if (token) fetchData(); }, [fetchData, token]);
  useEffect(() => { if (token && boardData) fetchData({ silent: true }); }, [version]); // eslint-disable-line

  // Auto-refresh every 30s while the page is visible; refetch when it becomes visible again.
  useEffect(() => {
    if (!token) return undefined;
    const id = setInterval(() => { if (!document.hidden) fetchData({ silent: true }); }, POLL_MS);
    const onVis = () => { if (!document.hidden) fetchData({ silent: true }); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [token, fetchData]);

  return (
    <div className="min-h-screen" style={{ background: theme === 'light' ? '#f5f5f7' : '#04060a', transition: 'background-color .45s ease' }} data-testid="gameplan-root" data-theme={theme}>
      <div className={cn('gpj', theme === 'light' && 'gpj-light', switching && 'gpj-theming')}>
      <div className="gpj-ambient" aria-hidden="true"><i /><i /><i /><b /></div>
      <div className="gpj-shell" style={{ paddingInline: "clamp(16px,3vw,40px)" }}>
        <header className="gpj-top">
          <div className="gpj-brand"><small>Executive Command Centre</small><strong>Game Plan Tracker</strong></div>
          <Link to="/" className="gpj-pill" style={{ textDecoration: 'none' }} data-testid="gpj-back-home">← Derwent AI</Link>
          <span className={cn('gpj-pill', stale && 'stale')} data-testid="gpj-live-pill"><span className="gpj-live-dot" style={stale ? { background: 'var(--amber)', animation: 'none' } : undefined} />{stale ? 'Live · showing last good data' : 'Live'}</span>
          {readOnly && <span className="gpj-pill" style={{ borderColor: 'var(--blue)', color: 'var(--blue)' }} data-testid="gpj-readonly-pill">View only</span>}
          <Clock />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          {!readOnly && (
          <button type="button" className="gpj-btn icon" onClick={() => { if (!cel?.settings?.track_name) { setShowGoalsDialog(true); toast.message('No alert track loaded yet', { description: 'Choose one under Celebration alerts in Set Goals.' }); return; } cel.sound.setLocalMuted(!cel.sound.localMuted); }} aria-pressed={!cel?.sound?.localMuted} title={!cel?.settings?.track_name ? 'No alert track loaded. Click to choose one.' : cel.sound.localMuted ? 'Alert sound muted on this screen. Click to turn on.' : `Alert sound on (${cel.settings.track_name}). Click to mute.`} data-testid="gpj-mute-btn">{cel?.sound?.localMuted || !cel?.settings?.on || !cel?.settings?.track_name ? <VolumeX /> : <Volume2 />}</button>
          )}
          {!readOnly && (
          <button type="button" className="gpj-btn" onClick={() => setShowGoalsDialog(true)} data-testid="open-goals-settings"><Target /> <span className="hidden sm:inline">Set Goals</span></button>
          )}
          {!readOnly && (
          <button type="button" className="gpj-btn primary" onClick={() => setShowCommandCentre(true)} data-testid="open-command-centre"><Tv /> <span className="hidden sm:inline">Performance TV</span><span className="sm:hidden">TV</span></button>
          )}
          <button type="button" className="gpj-btn icon" onClick={() => setShowPrintReport(true)} title="Print Report" data-testid="open-print-report"><Printer /></button>
        </header>

        <main className="pb-28">
          <div className="gpj-filters">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="gpj-select w-auto min-w-[170px]" data-testid="month-selector"><Calendar className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>{monthOptions.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={brokerFilter || 'all_brokers'} onValueChange={(v) => setBrokerFilter(v === 'all_brokers' ? '' : v)}>
              <SelectTrigger className="gpj-select w-auto min-w-[150px]" data-testid="broker-filter"><Users className="w-4 h-4 mr-2" /><SelectValue placeholder="All brokers" /></SelectTrigger>
              <SelectContent><SelectItem value="all_brokers">All brokers</SelectItem>{options.brokers.map((b) => <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sourceFilter || 'all_sources'} onValueChange={(v) => setSourceFilter(v === 'all_sources' ? '' : v)}>
              <SelectTrigger className="gpj-select w-auto min-w-[150px]" data-testid="source-filter"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent><SelectItem value="all_sources">All sources</SelectItem>{options.leadSources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" className={cn('gpj-btn icon', spinning && 'spin')} onClick={() => fetchData({ silent: true })} title="Refresh" data-testid="gpj-refresh-btn"><RefreshCw /></button>
            <span className="sp" />
            <span className="gpj-stamp" data-testid="gpj-updated">Updated {lastRefresh.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', second: '2-digit' })} · Auto-refresh 30s</span>
          </div>

          <Tabs active={activeView} onChange={setActiveView} />

          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--fg-3)' }} /></div>
          ) : (
            <div key={activeView} className="gpj-view">
              {activeView === 'dashboard' ? (
                <div data-testid="gameplan-merged-dashboard">
                  <CommandCentre month={selectedMonth} boardData={boardData} live={liveData} readOnly={readOnly} options={options} authToken={token}
                    brokerFilter={brokerFilter} sourceFilter={sourceFilter} refreshSignal={lastRefresh?.getTime?.()}
                    onRefresh={() => { fetchData({ silent: true }); bump(); }} onOpenFile={openFile} onEditDeal={openEditDeal} onStage={goToStage} />
                </div>
              ) : activeView === 'flow' ? (
                <PipelineTab live={liveData} onOpenFile={openFile} onStage={goToStage} initialStage={pipelineStage} focusSignal={pipelineFocus} />
              ) : activeView === 'lodged' ? (
                <ApprovalsView authToken={token} readOnly={readOnly} onOpenFile={openFile} refreshSignal={version} onChanged={bump} />
              ) : activeView === 'settlements' ? (
                <div className="gpj-grid">
                  <SettlementPanels live={liveData} />
                  <SettlementsView authToken={token} readOnly={readOnly} onOpenFile={openFile} refreshSignal={version} onChanged={bump} />
                </div>
              ) : activeView === 'live' ? (
                <LivePerformanceDashboard authToken={token} options={options} onOpenPrint={() => setShowPrintReport(true)} onOpenSetGoals={() => setShowGoalsDialog(true)} />
              ) : activeView === 'pipeline' ? (
                <PipelineCommandCentre authToken={token} options={options} refreshSignal={lastRefresh?.getTime?.()} />
              ) : activeView === 'metrics' ? (
                <MetricsView brokers={options.brokers} authToken={token} boardData={boardData} onRefresh={fetchData} />
              ) : (
                <ProjectionsView projectionsData={projectionsData} onUpdateDeal={fetchData} authToken={token} />
              )}
            </div>
          )}
          <div className="gpj-foot"><span data-testid="gp-brand-line">{brandLine(brand) || 'DerwentAI'}</span><span>Every figure is live system data · nothing is estimated</span></div>
        </main>
      </div>
      </div>

      <GoalsSettingsDialog open={showGoalsDialog} onOpenChange={setShowGoalsDialog} month={selectedMonth} authToken={token} onSaved={() => fetchData({ silent: true })} />
      <FilePanel token={token} readOnly={readOnly} dealId={panelDealId} open={!!panelDealId} onClose={() => setPanelDealId(null)} onChanged={bump} onOpenFull={(f) => { setPanelDealId(null); openFileOnBoard(f); }} onEditDeal={readOnly ? undefined : openEditDeal} />
      <AddDealDialog open={!!editingDeal} editDeal={editingDeal} onClose={() => setEditingDeal(null)} onSave={handleSaveDeal} options={options} month={selectedMonth} />
      <PrintReportDialog open={showPrintReport} onOpenChange={setShowPrintReport} />
      <PerformanceCommandCentre open={showCommandCentre} onClose={() => setShowCommandCentre(false)} onOpenSetGoals={() => { setShowCommandCentre(false); setShowGoalsDialog(true); }} />
    </div>
  );
};

const GamePlanTracker = () => {
  const { token } = useAuth();
  return <CelebrationProvider token={token}><GamePlanContent /></CelebrationProvider>;
};
export default GamePlanTracker;
